'use client';

import { useDialog } from '@musinsa/mds-ui';
import type { SubmitJoinApplicationServiceRequest } from '@musinsa/services-curator';
import {
  extractUserInput,
  platformIdToSnsType,
  snsTypeToPlatformId,
  updateSnsAccount,
  validateEtcUrl,
  validateSNSForm,
} from '@musinsa/services-curator';
import { useCallback, useMemo, useState } from 'react';
import { PLATFORM_INDEX, type PlatformIndex } from '../constants';
import { findPlatformById, type Platform } from '../constants/platforms';

interface UseSNSFormProps {
  formValues: SubmitJoinApplicationServiceRequest;
  onUpdate: (values: Partial<SubmitJoinApplicationServiceRequest>) => void;
}

export const useSNSForm = ({ formValues, onUpdate }: UseSNSFormProps) => {
  const dialog = useDialog();
  const [firstDropdownOpen, setFirstDropdownOpen] = useState(false);
  const [secondDropdownOpen, setSecondDropdownOpen] = useState(false);
  const [etcUrlErrors, setEtcUrlErrors] = useState<{
    [key: number]: string | null;
  }>({});

  const showSecondPlatform = formValues.snsAccounts.length > 1;

  const canProceed = useMemo(() => {
    const basicValidation = validateSNSForm(formValues);
    const hasEtcUrlError = Object.values(etcUrlErrors).some((error) => error !== null);
    return basicValidation && !hasEtcUrlError;
  }, [formValues, etcUrlErrors]);

  const checkPlatformDuplicate = useCallback(
    (index: PlatformIndex, selectedPlatform: Platform): boolean => {
      const otherIndex =
        index === PLATFORM_INDEX.FIRST_PLATFORM
          ? PLATFORM_INDEX.SECOND_PLATFORM
          : PLATFORM_INDEX.FIRST_PLATFORM;
      const otherAccount = formValues.snsAccounts[otherIndex];

      if (!otherAccount?.snsType) return false;

      const otherPlatformId = snsTypeToPlatformId(otherAccount.snsType);
      return selectedPlatform.id === otherPlatformId;
    },
    [formValues.snsAccounts],
  );

  const handleDuplicateSelection = useCallback(
    (index: PlatformIndex) => {
      dialog.open({
        title: '플랫폼 별 1개의 정보만 제출 가능합니다.',
        confirmButtonText: '확인',
        onConfirm: () => {
          if (index === PLATFORM_INDEX.SECOND_PLATFORM) {
            setSecondDropdownOpen(true);
          } else {
            setFirstDropdownOpen(true);
          }
        },
      });
    },
    [dialog, setFirstDropdownOpen, setSecondDropdownOpen],
  );

  const updateAccount = useCallback(
    (
      index: PlatformIndex,
      updates: Partial<SubmitJoinApplicationServiceRequest['snsAccounts'][0]>,
    ) => {
      const updatedAccounts = updateSnsAccount(formValues.snsAccounts, index, updates);
      onUpdate({ snsAccounts: updatedAccounts });
    },
    [formValues.snsAccounts, onUpdate],
  );

  const onPlatformSelect = useCallback(
    (index: PlatformIndex, platform: Platform) => {
      if (checkPlatformDuplicate(index, platform)) {
        handleDuplicateSelection(index);
        return;
      }

      const snsType = platformIdToSnsType(platform.id);
      updateAccount(index, { snsType });

      if (snsType === 'ETC') {
        const currentUrl = formValues.snsAccounts[index]?.url || '';
        if (currentUrl.trim()) {
          const validation = validateEtcUrl(currentUrl);
          setEtcUrlErrors((prev) => ({
            ...prev,
            [index]: validation.isValid
              ? null
              : validation.message || '올바른 URL 형식이 아닙니다.',
          }));
        }
      } else {
        setEtcUrlErrors((prev) => ({
          ...prev,
          [index]: null,
        }));
      }
    },
    [updateAccount, checkPlatformDuplicate, handleDuplicateSelection, formValues.snsAccounts],
  );

  const onUrlChange = useCallback(
    (index: PlatformIndex, url: string) => {
      updateAccount(index, { url });

      const account = formValues.snsAccounts[index];
      if (account?.snsType === 'ETC') {
        const validation = validateEtcUrl(url);
        setEtcUrlErrors((prev) => ({
          ...prev,
          [index]: validation.isValid ? null : validation.message || '올바른 URL 형식이 아닙니다.',
        }));
      } else {
        setEtcUrlErrors((prev) => ({
          ...prev,
          [index]: null,
        }));
      }
    },
    [updateAccount, formValues.snsAccounts],
  );

  const onUrlClear = useCallback(
    (index: PlatformIndex) => {
      updateAccount(index, { url: '' });
    },
    [updateAccount],
  );

  const addSecondPlatform = useCallback(() => {
    if (formValues.snsAccounts.length === 1) {
      const updatedAccounts = [...formValues.snsAccounts, { snsType: null, url: '' }];
      onUpdate({ snsAccounts: updatedAccounts });
    }
  }, [formValues.snsAccounts, onUpdate]);

  const removeSecondPlatform = useCallback(() => {
    const updatedAccounts = formValues.snsAccounts.slice(0, 1);
    onUpdate({ snsAccounts: updatedAccounts });
  }, [formValues.snsAccounts, onUpdate]);

  const getAccountData = useCallback(
    (index: PlatformIndex) => {
      const account = formValues.snsAccounts[index];
      if (!account) {
        return {
          selectedPlatform: undefined,
          platformUrl: '',
        };
      }

      const selectedPlatform =
        account.snsType && account.snsType !== null
          ? findPlatformById(snsTypeToPlatformId(account.snsType))
          : undefined;

      const platformUrl =
        selectedPlatform && account.url && account.snsType !== 'ETC'
          ? extractUserInput(selectedPlatform, account.url)
          : account.url || '';

      return {
        selectedPlatform,
        platformUrl,
      };
    },
    [formValues.snsAccounts],
  );

  return {
    // 상태
    showSecondPlatform,
    canProceed,

    // 드롭다운 상태
    firstDropdownOpen,
    setFirstDropdownOpen,
    secondDropdownOpen,
    setSecondDropdownOpen,

    // 계정별 데이터 접근
    getAccountData,

    // 통합 핸들러들
    onPlatformSelect,
    onUrlChange,
    onUrlClear,

    // 플랫폼 관리
    addSecondPlatform,
    removeSecondPlatform,

    // ETC URL 검증
    etcUrlErrors,
  };
};
